import { cn } from '../../lib/cn.ts';
import { Button } from '../button.tsx';

export interface DraftRow {
  label: string;
  value: string;
}

export interface DraftReservationCardProps {
  title: string;
  statusLabel?: string;
  rows: DraftRow[];
  confirmLabel: string;
  holdLabel: string;
  onConfirm?: () => void;
  onHold?: () => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * DraftReservationCard — an AI-drafted reservation waiting for a human Confirm
 * or Hold. The accent (warm-stone) lives only on the Confirm action.
 */
export function DraftReservationCard({
  title,
  statusLabel,
  rows,
  confirmLabel,
  holdLabel,
  onConfirm,
  onHold,
  disabled = false,
  className,
  'data-testid': testId,
}: DraftReservationCardProps) {
  return (
    <section
      data-testid={testId}
      className={cn(
        'flex flex-col gap-4 rounded-3xl border border-[var(--color-border)] bg-[var(--color-alabaster)] p-5 shadow-[var(--shadow-card)]',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-[var(--font-display)] text-base font-medium tracking-tight text-[var(--color-charcoal)]">
          {title}
        </h3>
        {statusLabel && (
          <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)]">
            {statusLabel}
          </span>
        )}
      </div>
      <dl className="flex flex-col">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] py-2.5 last:border-0"
          >
            <dt className="font-[var(--font-mono)] text-[11px] uppercase tracking-wide text-[var(--color-stone-mid)]">
              {row.label}
            </dt>
            <dd className="text-sm font-medium text-[var(--color-charcoal)]">{row.value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex items-center gap-2">
        <Button
          data-testid="draft-reservation-confirm-btn"
          onClick={onConfirm}
          disabled={disabled}
          size="sm"
          className="flex-1 border-[var(--color-warm-stone)] bg-[var(--color-warm-stone)] text-[var(--color-charcoal)] hover:bg-[var(--color-warm-stone-deep)]"
        >
          {confirmLabel}
        </Button>
        <Button
          data-testid="draft-reservation-hold-btn"
          onClick={onHold}
          disabled={disabled}
          size="sm"
          variant="secondary"
          className="flex-1"
        >
          {holdLabel}
        </Button>
      </div>
    </section>
  );
}
